import { React, useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@apollo/client";

import { INSURANCE_CARRIERS } from "../../../graphql/query/query";
import { InsuranceCard } from "./card/InsuranceCard.component";
import VerificationLayout from "../../layout/VerificationLayout.component";
import AppButton from "../../base/AppButton.component";
import { useNavigate } from "react-router-dom";
import { useVerification } from "../../../context/Verification.context";
import Theme from "../../../utils/Theme";
import { navigateContinueDelivery, getContinueDeliveryNavTarget } from "../../../utils/videoPlayer";


const InsuranceProviderComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [initialCarrierCards, setInitialCarrierCards] = useState([]);
  const [carrierCards, setCarrierCards] = useState([]);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  const navigate = useNavigate();
  const verification = useVerification();

  const { loading } = useQuery(INSURANCE_CARRIERS, {
    variables: {
      carrierInput: {
        transaction_id: verification?.customer?.transactionId,
        first_name: verification?.customer?.firstName,
        last_name: verification?.customer?.lastName,
        email: verification?.customer?.email,
        mobile_number: verification?.customer?.mobileNumber,
        vin: verification?.customer?.vin,
        employee_user_id: parseInt(verification?.employeeId),
        user_id: verification?.customer?.userId,
        dealership_id: parseInt(verification?.dealershipId),
      },
    },
    skip: !verification?.customer?.transactionId,
    onCompleted: (data) => {
      setHasDataLoaded(true);
      if (data?.insuranceCarrier?.Carrier?.length > 0) {
        setCarrierCards(data.insuranceCarrier.Carrier);
        setInitialCarrierCards(data.insuranceCarrier.Carrier);
      }
    },
    onError: () => {
      setHasDataLoaded(true);
    },
  });

  const cards = useMemo(
    () =>
      carrierCards.map((carrier, index) => (
        <InsuranceCard key={index} carrier={carrier} />
      )),
    [carrierCards],
  );

  useEffect(() => {
    setCarrierCards(
      initialCarrierCards.filter((card) =>
        card.name.toUpperCase().includes(searchText.toUpperCase()),
      ),
    );
  }, [searchText, initialCarrierCards]);

  const showSearchMessage =
    searchText && carrierCards.length === 0 && hasDataLoaded;
  const showInitialNoResults =
    !loading && hasDataLoaded && initialCarrierCards.length === 0;

  // Continue Delivery → quote/thankYou first, then KW + artifacts if needed
  const handleContinueDelivery = () => {
    navigateContinueDelivery(
      navigate,
      verification,
      getContinueDeliveryNavTarget(verification),
    );
  };

  return (
    <VerificationLayout>
      {/* HEADER + SEARCH */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Typography
          align="start"
          sx={{ mt: "32px", fontWeight: "bold", ml: 1, fontSize: "18px" }}
        >
          Verify Your Insurance
        </Typography>
        <Typography align="start" sx={{ ml: 1 }}>
          Recommended for the faster and smoother delivery.
        </Typography>
        <FormControl sx={{ m: 1, mt: "15px" }} variant="outlined">
          <InputLabel>Search Provider</InputLabel>
          <OutlinedInput
            label="Search Provider"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>

      {/* CARRIER LIST */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : carrierCards.length > 0 ? (
          <Box
            sx={{
              width: window.innerWidth <= 750 ? "100%" : "80%",
              overflowY: "auto",
              px: 1,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {cards}
          </Box>
        ) : showSearchMessage ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              width: "100%",
              pl: 3,
              mt: "60px",
            }}
          >
            <Typography
              sx={{
                color: "black",
                fontSize: "20px",
                textAlign: "center",
                width: "100%",
              }}
            >
              We couldn't find a match yet.
            </Typography>
            <Typography
              sx={{
                color: "black",
                fontSize: "20px",
                textAlign: "center",
                width: "100%",
              }}
            >
              Try checking spelling or using a different name.
            </Typography>
          </Box>
        ) : showInitialNoResults ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              p: 3,
            }}
          >
            <Typography
              sx={{
                color: "black",
                fontSize: "20px",
                fontWeight: "bold",
                textAlign: "center",
                mb: 2,
              }}
            >
              Searching for insurance providers...
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "16px",
                textAlign: "center",
              }}
            >
              Please check back soon or continue with manual verification.
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* BOTTOM BUTTON */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
         height: {
  xs: "100px",   // mobile
  sm: "100px",   // tablet
  md: "100px",   // desktop
  lg: "100px",
  xl: "100px",
},
          zIndex: 10,
          pt: 1,
          pb: 1,
          backgroundColor: "white",
        }}
      >
        <AppButton
          sx={{
            width: {
              xs: "70%",
              sm: "70%",
              md: "40%",
              lg: "40%",
              xl: "40%",
            },
            fontWeight: "bold",
            textTransform: "none",
            borderRadius: 0,
            color: "black",
            backgroundColor: Theme.palette.secondary.main,
            height: "100%",
            flexDirection: "column",
          }}
          buttonText="CONTINUE DELIVERY"
          helperText="(You can finalize insurance manually later)"
          size="large"
          fontWeight="bold"
          variant="text"
          clickHandler={handleContinueDelivery}
        />
      </Box>
    </VerificationLayout>
  );
};

export default InsuranceProviderComponent;